import { didApiUrl } from '@/environment';
import { authenticatedAxios } from '@/services/auth';
import {
    ExternalKeysProviders,
    GetExternalKeysResponseDto,
    IAgentKey,
    ILogoRequest,
    ISettingsRequest,
    ISettingsResponse,
    SanitizedSecretDto,
    UpsertExternalKeysDto,
} from '@d-id/types';
import { AxiosError } from 'axios';

type CreditRecord = {
    valid_from: string;
    created_at: string;
    expire_at: string;
    modified_at: string;
    owner_id: string;
    plan_group: string;
    price_id: string;
    product_billing_interval: string;
    product_id: string;
    remaining: number;
    total: number;
};

export type Credit = { credits?: CreditRecord[]; remaining: number; total: number; expire_at: string; isExpired: boolean };

export const getUserCredits = async (includeExpired: boolean = false): Promise<Credit> => {
    const response = await authenticatedAxios.get(`${didApiUrl}/credits`, {
        params: { include_expired: includeExpired },
    });
    return response.data;
};

export const sendEmailVerification = async (): Promise<void> => {
    await authenticatedAxios.get(`${didApiUrl}/users/send-email-verification`);
};

export const deleteAccount = async () => {
    await authenticatedAxios.delete(`${didApiUrl}/users`);
};

const getClientKey = async (useCase?: string) => {
    const url = `${didApiUrl}/users/client-keys${useCase ? `?use_case=${useCase}` : ''}`;
    const response = await authenticatedAxios?.get(url);

    return response?.data;
};

const createClientKey = async (agentKeyRequest: IAgentKey) => {
    const response = await authenticatedAxios?.post(`${didApiUrl}/users/client-keys`, agentKeyRequest);
    return response?.data;
};

export const patchClientKey = async (agentKeyRequest: IAgentKey) => {
    const response = await authenticatedAxios?.patch(`${didApiUrl}/users/client-keys`, agentKeyRequest);
    return response?.data;
};

export const getOrCreateClientKey = async (agentKeyRequest: IAgentKey) => {
    try {
        const clientKey = await getClientKey(agentKeyRequest.use_case);
        if (clientKey) {
            return clientKey;
        }
    } catch (e) {
        if ((e as AxiosError)?.response?.status !== 404) {
            // Throw if error is not related to key not found
            throw e;
        }
    }
    return await createClientKey(agentKeyRequest);
};

export const upsertExternalKey = async (data: { provider: ExternalKeysProviders; secret: string }): Promise<UpsertExternalKeysDto> => {
    const response = await authenticatedAxios?.post(`${didApiUrl}/external-keys/${data.provider}`, data);
    return response?.data;
};
export const deleteExternalKey = async (provider: ExternalKeysProviders): Promise<void> => {
    const response = await authenticatedAxios?.delete(`${didApiUrl}/external-keys/${provider}`);
    return response?.data;
};

export const getExternalKey = async (provider: ExternalKeysProviders): Promise<GetExternalKeysResponseDto> => {
    const response = await authenticatedAxios.get(`${didApiUrl}/external-keys/${provider}`);
    return response.data;
};

export const getLogo = async (): Promise<ILogoRequest> => {
    const response = await authenticatedAxios?.get(`${didApiUrl}/settings/logo`);
    return response?.data;
};

export const uploadLogo = async (data: { file: File; top: number; left: number }) => {
    const formData = new FormData();
    formData.append('logo', data.file);

    formData.append('top', `${data.top || 0}`);
    formData.append('left', `${data.left || 0}`);

    const response = await authenticatedAxios?.post(`${didApiUrl}/settings/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response?.data;
};

export const updateSettings = async (data: ISettingsRequest): Promise<ISettingsResponse> => {
    const response = await authenticatedAxios?.post(`${didApiUrl}/settings`, data);
    return response?.data;
};

export const getSettings = async (): Promise<ISettingsResponse> => {
    const response = await authenticatedAxios?.get(`${didApiUrl}/settings`);
    return response?.data;
};

export const deleteLogo = async (): Promise<void> => {
    const response = await authenticatedAxios?.delete(`${didApiUrl}/settings/logo`);
    return response?.data;
};

export const getSecrets = async (provider: ExternalKeysProviders): Promise<GetExternalKeysResponseDto[]> => {
    const response = await authenticatedAxios?.get(`${didApiUrl}/secrets/provider/${provider}`);
    return response?.data;
};

export const upsertSecret = async (data: {
    provider: ExternalKeysProviders;
    secret: string;
    endpoint?: string;
    model?: string;
    isStreaming?: boolean;
    maxMessages?: number;
}): Promise<SanitizedSecretDto> => {
    const response = await authenticatedAxios?.post(`${didApiUrl}/secrets`, {
        provider: data.provider,
        secret: data.secret,
        endpoint: data.endpoint,
        model: data.model,
        isStreaming: data.isStreaming,
        maxMessages: data.maxMessages,
    });
    return response?.data;
};

export const deleteSecret = async (id: string): Promise<void> => {
    const response = await authenticatedAxios?.delete(`${didApiUrl}/secrets/${id}`);
    return response?.data;
};

export const getSecretById = async (id: string): Promise<SanitizedSecretDto> => {
    const response = await authenticatedAxios?.get(`${didApiUrl}/secrets/${id}`);
    return response?.data;
};
